import { Route, Routes } from 'react-router-dom';

import * as Sentry from '@sentry/react';

import { Routes as AppRoutes, useDarkTheme } from '@utilities';

import * as Loadables from './loadables';

import './assets/styles/app.scss';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

/**
 * Because of SEO optimization we pre-render all pages.
 * This results in folder structure which contains one root `index.html` file
 * and several nested `index.html` files in each of the pages directories
 * (see "/dist" folder). GCP (Google Cloud Platform) has an issue
 * (https://cloud.google.com/storage/docs/static-website#three-object_bucket)
 * which causes our fallback route to be rendered when a page is accessed
 * without a trailing slash. Adding wildcard to each of the pages solves this
 * problem.
 *
 * Let's say that we want to open the "/about" page directly.
 * What happens is the following:
 * - the user requests the page at "https://dmarc.io/about"
 * - GCP redirects this to "https://dmarc.io/about/index.html"
 * - the React app is started and React Router tries to load the component
 *   which corresponds to the "about/index.html" route. As there is none, it
 *   renders the fallback route at "*" (the last one below).
 * - adding the wildcard to each route tells React Router to load the
 *   corresponding component regardless of what's after the trailing slash.
 *
 * Note: This may lead to unexpected behavior, such as
 * "https://dmarc.io/about/non-existing-url rendering the same content
 * as "https://dmarc.io/about/" instead of a 404, but that's okay at this point.
 */
export const App = () => {
	useDarkTheme(true);

	return (
		<SentryRoutes>
			<Route path={AppRoutes.BASE} element={<Loadables.Home />} />
			<Route path={`${AppRoutes.SOURCE}/:source`} element={<Loadables.Sources />} />
			<Route path={`${AppRoutes.SOURCES}/*`} element={<Loadables.Sources />} />
			<Route path={`${AppRoutes.FORWARDER}/:forwarder`} element={<Loadables.Forwarders />} />
			<Route path={`${AppRoutes.FORWARDERS}/*`} element={<Loadables.Forwarders />} />
			<Route path={`${AppRoutes.SOBOO}/*`} element={<Loadables.Soboo />} />
			<Route path={`${AppRoutes.ABOUT}/*`} element={<Loadables.About />} />
			<Route path="*" element={<Loadables.Sources />} />
		</SentryRoutes>
	);
};
